import React, { useEffect, useState, useCallback } from "react";
import { API_URL } from "../config";
import ImageSlider from "../components/ImageSlider";
import "./FaceMaker.css";

const TEXT_FIELDS = [
  { key: "character_type", label: "Character Type" },
  { key: "illustration_style", label: "Illustration Style" },
  { key: "framing", label: "Framing" },
  { key: "variations", label: "Variations" },
  { key: "apparent_age", label: "Apparent Age" },
  { key: "gender", label: "Gender" },
  { key: "ethnicity", label: "Ethnicity" },
  { key: "clothing_items", label: "Clothing Items" },
  { key: "accessories", label: "Accessories" },
];

const IMAGE_FIELDS = [
  { key: "height_build", label: "Height & Build" },
  { key: "facial_structure", label: "Facial Structure" },
  { key: "mood", label: "Mood" },
  { key: "skin_tone", label: "Skin Tone" },
  { key: "eye_color", label: "Eye Color" },
  { key: "hair_style", label: "Hair Style" },
  { key: "facial_features", label: "Facial Features" },
];

function authHeaders() {
  const token = localStorage.getItem("token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function FaceMaker() {
  const [textOptions, setTextOptions] = useState({});
  const [references, setReferences] = useState({});
  const [loadingRefs, setLoadingRefs] = useState({});
  const [selection, setSelection] = useState({});
  const [prompt, setPrompt] = useState("");
  const [expanding, setExpanding] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [imageB64, setImageB64] = useState("");
  const [imageMime, setImageMime] = useState("image/png");
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // Cargar dimensiones (opciones de texto) y todas las referencias
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dimResp = await fetch(`${API_URL}/facemaker/dimensions`);
        if (!dimResp.ok) throw new Error("No se pueden cargar dimensiones");
        const dimData = await dimResp.json();
        if (cancelled) return;
        setTextOptions(dimData.text_options || {});
        // Defaults sensatos
        setSelection((prev) => ({
          character_type: dimData.text_options?.character_type?.[0] || "",
          illustration_style: "Hyperrealistic",
          framing: "Full body",
          variations: "6",
          ...prev,
        }));
      } catch (e) {
        setError(`Error cargando dimensiones: ${e.message}`);
      }

      // Cargar referencias por cada dimension visual en paralelo
      const tasks = IMAGE_FIELDS.map(async ({ key }) => {
        setLoadingRefs((s) => ({ ...s, [key]: true }));
        try {
          const r = await fetch(`${API_URL}/facemaker/references?dimension=${key}`);
          const d = await r.json();
          // Si la url es relativa (modo local), prepender API_URL
          const items = (d.items || []).map((it) => ({
            ...it,
            url: it.url && it.url.startsWith("/") ? `${API_URL}${it.url}` : it.url,
          }));
          if (!cancelled) setReferences((s) => ({ ...s, [key]: items }));
        } catch (e) {
          if (!cancelled) setReferences((s) => ({ ...s, [key]: [] }));
        } finally {
          if (!cancelled) setLoadingRefs((s) => ({ ...s, [key]: false }));
        }
      });
      await Promise.all(tasks);
    })();
    return () => { cancelled = true; };
  }, []);

  const updateText = (key, value) => {
    setSelection((s) => ({ ...s, [key]: value }));
    setPrompt("");
  };

  const updateImage = (key, refId) => {
    setSelection((s) => ({ ...s, [key]: refId }));
    setPrompt("");
  };

  // Genera el prompt, lo guarda en state y lo devuelve. Devuelve "" en caso de error.
  const buildPrompt = useCallback(async () => {
    setError("");
    setInfo("");
    setExpanding(true);
    try {
      const resp = await fetch(`${API_URL}/facemaker/expand-prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ selection }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt);
      }
      const data = await resp.json();
      const generated = data.prompt || "";
      setPrompt(generated);
      setInfo(`Prompt generado (${data.tokens?.input}+${data.tokens?.output} tokens)`);
      return generated;
    } catch (e) {
      setError(`Error generando prompt: ${e.message}`);
      return "";
    } finally {
      setExpanding(false);
    }
  }, [selection]);

  const downloadImage = useCallback(() => {
    if (!imageB64) return;
    const ext = imageMime.includes("jpeg") ? "jpg" : "png";
    const a = document.createElement("a");
    a.href = `data:${imageMime};base64,${imageB64}`;
    a.download = `facemaker_${Date.now()}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [imageB64, imageMime]);

  const saveToLibrary = useCallback(async () => {
    if (!imageB64) return;
    setSaving(true);
    setError("");
    try {
      const resp = await fetch(`${API_URL}/facemaker/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ image_b64: imageB64, prompt }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt);
      }
      const data = await resp.json();
      setSavedId(data.id);
      setInfo(`Guardado en biblioteca como ${data.id}`);
    } catch (e) {
      setError(`Error guardando: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }, [imageB64, prompt]);

  const generateImage = useCallback(async () => {
    setError("");
    setInfo("");
    let effectivePrompt = prompt.trim();
    if (!effectivePrompt) {
      effectivePrompt = (await buildPrompt()).trim();
      if (!effectivePrompt) return; // buildPrompt ya seteo el error
    }
    setGenerating(true);
    setImageB64("");
    try {
      const resp = await fetch(`${API_URL}/facemaker/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ prompt: effectivePrompt }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt);
      }
      const data = await resp.json();
      setImageB64(data.image_b64);
      setImageMime(data.mime_type || "image/png");
      setSavedId("");
      setInfo(`Imagen generada con ${data.model}`);
    } catch (e) {
      setError(`Error generando imagen: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  }, [prompt, buildPrompt]);

  const busy = expanding || generating || saving;

  return (
    <div className="facemaker">
      <header className="facemaker-header">
        <h1>Face Maker</h1>
        <p>Genera fichas de personaje con 6 propuestas en una sola imagen.</p>
      </header>

      <div className={`facemaker-body${busy ? " is-busy" : ""}`} aria-busy={busy}>
        {/* COLUMNA IZQUIERDA — selectores */}
        <aside className="facemaker-selectors">
          <section className="facemaker-section">
            <h2>Atributos</h2>
            <div className="facemaker-dropdowns">
              {TEXT_FIELDS.map(({ key, label }) => (
                <label className="facemaker-dropdown" key={key}>
                  <span>{label}</span>
                  <select
                    value={selection[key] || ""}
                    onChange={(e) => updateText(key, e.target.value)}
                  >
                    <option value="">— sin elegir —</option>
                    {(textOptions[key] || []).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          </section>

          <section className="facemaker-section">
            <h2>Referencias visuales</h2>
            {IMAGE_FIELDS.map(({ key, label }) => (
              <ImageSlider
                key={key}
                label={label}
                items={references[key] || []}
                selectedId={selection[key] || null}
                onSelect={(id) => updateImage(key, id)}
                loading={loadingRefs[key]}
                emptyText="Sin referencias ingestadas todavia"
              />
            ))}
          </section>
        </aside>

        {/* COLUMNA DERECHA — prompt + imagen */}
        <main className="facemaker-output">
          <section className="facemaker-section">
            <div className="facemaker-actions">
              <button
                className="btn-primary"
                onClick={buildPrompt}
                disabled={busy}
              >
                {expanding ? "Generando prompt..." : "Generar prompt"}
              </button>
              <button
                className="btn-secondary"
                onClick={generateImage}
                disabled={busy}
              >
                {generating ? "Generando imagen..." : "Generar imagen"}
              </button>
            </div>

            {error && <div className="facemaker-error">{error}</div>}
            {info && !error && <div className="facemaker-info">{info}</div>}
          </section>

          <section className="facemaker-section">
            <h2>Prompt (editable)</h2>
            <textarea
              className="facemaker-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Pulsa 'Generar prompt' para que Claude redacte el prompt a partir de tu seleccion. Puedes editarlo antes de generar la imagen."
              rows={10}
            />
          </section>

          <section className="facemaker-section">
            <h2>Imagen final</h2>
            <div className="facemaker-canvas">
              {generating && <div className="facemaker-placeholder">Generando con Nano Banana Pro...</div>}
              {!generating && imageB64 && (
                <img src={`data:${imageMime};base64,${imageB64}`} alt="Personaje generado" />
              )}
              {!generating && !imageB64 && (
                <div className="facemaker-placeholder">Sin imagen todavia.</div>
              )}
            </div>
            {imageB64 && !generating && (
              <div className="facemaker-actions" style={{ marginTop: 10 }}>
                <button className="btn-secondary" onClick={downloadImage}>
                  Descargar
                </button>
                <button
                  className="btn-secondary"
                  onClick={saveToLibrary}
                  disabled={saving || !!savedId}
                >
                  {saving
                    ? "Guardando..."
                    : savedId
                    ? `Guardado ${savedId}`
                    : "Guardar en biblioteca"}
                </button>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
